import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState, useCallback, } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import { Slice } from "@tiptap/pm/model";
import StarterKit from "@tiptap/starter-kit";
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import { debounce } from "lodash";
import DOMPurify from "dompurify";
import { SketchPicker } from "react-color";
import { html as beautifyHtml } from "js-beautify";
import filepicker from "filepicker-js";
import allowedImageMimetypes from "helpers/allowedImageMimetypes";
import { filestackEndpoints, sanitizeUrl } from "helpers/filestackUrl";
import { S3_URL, FILEPICKER_API_KEY, FILESTACK_IMAGE_PATH, fit, } from "../../helpers/constants";
import "./_style.scss";
import { FULL_TOOLBAR, SIMPLE_TOOLBAR, EMAIL_TOOLBAR, MESSAGE_TOOLBAR, CAMPAIGN_UPDATE_TOOLBAR, MARKDOWN_TOOLBAR, COLORS, EMOJIS, isApprovedEmbedUrl, toEmbedUrl, extractDocumentColors, stripNonBreakingSpaces, } from "./utils";
import { BulletListIcon, OrderedListIcon, AlignLeftIcon, AlignCenterIcon, AlignRightIcon, ImageIcon, MediaIcon, LinkIcon, HrIcon, EmojiIcon, CodeIcon, UndoIcon, RedoIcon, } from "./icons";
export { FULL_TOOLBAR, SIMPLE_TOOLBAR, EMAIL_TOOLBAR, MESSAGE_TOOLBAR, CAMPAIGN_UPDATE_TOOLBAR, MARKDOWN_TOOLBAR, };
filepicker.setKey(FILEPICKER_API_KEY !== null && FILEPICKER_API_KEY !== void 0 ? FILEPICKER_API_KEY : "");
// --- Custom IFrame node ---
const IFrame = Node.create({
    name: "iframe",
    group: "block",
    atom: true,
    addAttributes() {
        return {
            src: { default: null },
            width: { default: "100%" },
            height: { default: "315" },
            frameborder: { default: "0" },
            allowfullscreen: { default: "true" },
        };
    },
    parseHTML() {
        return [{ tag: "iframe[src]" }];
    },
    renderHTML({ HTMLAttributes }) {
        return ["iframe", mergeAttributes(HTMLAttributes)];
    },
});
// --- Constants ---
const imageWidth = 685;
const pickerOptions = {
    container: "modal",
    services: ["CONVERT", "COMPUTER"],
    conversions: ["crop", "rotate"],
    mimetype: allowedImageMimetypes,
};
const storeOptions = {
    location: "S3",
    storeContainer: undefined,
    path: FILESTACK_IMAGE_PATH,
};
const ToolbarModal = ({ title, label, placeholder, initialValue = "", error, onConfirm, onCancel, }) => {
    const [value, setValue] = useState(initialValue);
    const inputRef = useRef(null);
    useEffect(() => {
        var _a, _b;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.select();
    }, []);
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            onConfirm(value);
        }
        if (e.key === "Escape") {
            onCancel();
        }
    };
    return (_jsx("div", { className: "text-editor__modal-overlay", onMouseDown: (e) => e.target === e.currentTarget && onCancel(), children: _jsxs("div", { className: "text-editor__modal", children: [_jsx("h4", { className: "text-editor__modal-title", children: title }), _jsx("label", { className: "text-editor__modal-label", htmlFor: "text-editor-modal-input", children: label }), _jsx("input", { ref: inputRef, id: "text-editor-modal-input", type: "text", className: "text-editor__modal-input", value: value, placeholder: placeholder, onChange: (e) => setValue(e.target.value), onKeyDown: handleKeyDown }), error && _jsx("p", { className: "text-editor__modal-error", children: error }), _jsxs("div", { className: "text-editor__modal-actions", children: [_jsx("button", { type: "button", className: "text-editor__modal-btn --primary", onClick: () => onConfirm(value), children: "OK" }), _jsx("button", { type: "button", className: "text-editor__modal-btn", onClick: onCancel, children: "Cancel" })] })] }) }));
};
const Tbtn = ({ active, title, onMouseDown, children, }) => (_jsx("button", { type: "button", className: active ? "--active" : undefined, title: title, onMouseDown: onMouseDown, children: children }));
const Tsep = () => _jsx("div", { className: "text-editor__toolbar-sep" });
// --- Component ---
const WysiwygEditor = ({ initialValue, value, onChange, onBlur, minHeight = 350, maxHeight = 1000, toolbar = FULL_TOOLBAR, }) => {
    var _a;
    const [attemptedImgUpload, setAttemptedImgUpload] = useState(false);
    const [uploadError, setUploadError] = useState(false);
    const [showEmojis, setShowEmojis] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showSketchPicker, setShowSketchPicker] = useState(false);
    const [sketchColor, setSketchColor] = useState("#000000");
    const savedSelectionRef = useRef(null);
    const [documentColors, setDocumentColors] = useState([]);
    useEffect(() => {
        var _a;
        const html = (_a = value !== null && value !== void 0 ? value : initialValue) !== null && _a !== void 0 ? _a : "";
        if (!html) {
            return;
        }
        setDocumentColors((prev) => {
            const newColors = extractDocumentColors(html, [...COLORS, ...prev]);
            return newColors.length > 0 ? [...prev, ...newColors] : prev;
        });
    }, [value, initialValue]);
    const [showSource, setShowSource] = useState(false);
    const [sourceHtml, setSourceHtml] = useState("");
    const [modal, setModal] = useState(null);
    const [activeBlock, setActiveBlock] = useState("p");
    const [, setActiveMarks] = useState(null);
    const has = (item) => toolbar.includes(item);
    const hasAny = (...items) => items.some(has);
    const debounceOnChange = useRef(debounce((html) => {
        const clean = DOMPurify.sanitize(html, {
            ADD_TAGS: ["iframe"],
            ADD_ATTR: ["target", "rel", "allowfullscreen", "frameborder", "src"],
        });
        // Strip any iframes whose src isn't an approved YouTube/Vimeo embed
        const doc = new DOMParser().parseFromString(clean, "text/html");
        doc.querySelectorAll("iframe").forEach((frame) => {
            var _a;
            if (!isApprovedEmbedUrl((_a = frame.getAttribute("src")) !== null && _a !== void 0 ? _a : "")) {
                frame.remove();
            }
        });
        onChange === null || onChange === void 0 ? void 0 : onChange(doc.body.innerHTML);
    }, 1000)).current;
    useEffect(() => () => {
        debounceOnChange.cancel();
    }, [debounceOnChange]);
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ link: false, bold: false, italic: false }),
            // Pasting plain text containing literal `*`/`_` (e.g. "Save 50%*") must not be
            // interpreted as markdown emphasis syntax, which would silently eat the characters.
            Bold.extend({ addPasteRules: () => [] }),
            Italic.extend({ addPasteRules: () => [] }),
            Link.configure({ openOnClick: false, HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" } }),
            Image,
            IFrame,
            TextStyle,
            Color.configure({ types: ["textStyle"] }),
            TextAlign.configure({ types: ["heading", "paragraph"] }),
        ],
        content: (_a = initialValue !== null && initialValue !== void 0 ? initialValue : value) !== null && _a !== void 0 ? _a : "",
        onCreate: ({ editor: ed }) => {
            const html = ed.getHTML();
            if (html && html !== "<p></p>") {
                setDocumentColors((prev) => {
                    const newColors = extractDocumentColors(html, [...COLORS, ...prev]);
                    return newColors.length > 0 ? [...prev, ...newColors] : prev;
                });
            }
        },
        onBlur: ({ editor: ed }) => {
            debounceOnChange.cancel();
            onChange === null || onChange === void 0 ? void 0 : onChange(ed.getHTML());
            onBlur === null || onBlur === void 0 ? void 0 : onBlur();
        },
        onUpdate: ({ editor: ed }) => debounceOnChange(ed.getHTML()),
        onSelectionUpdate: ({ editor: ed }) => {
            let block = "p";
            if (ed.isActive("heading", { level: 1 })) {
                block = "h1";
            }
            else if (ed.isActive("heading", { level: 2 })) {
                block = "h2";
            }
            else if (ed.isActive("heading", { level: 3 })) {
                block = "h3";
            }
            else if (ed.isActive("heading", { level: 4 })) {
                block = "h4";
            }
            else if (ed.isActive("heading", { level: 5 })) {
                block = "h5";
            }
            else if (ed.isActive("heading", { level: 6 })) {
                block = "h6";
            }
            else if (ed.isActive("codeBlock")) {
                block = "pre";
            }
            setActiveBlock((prev) => (prev !== block ? block : prev));
            setActiveMarks(ed.state.selection);
        },
        editorProps: {
            handlePaste: (_view, event) => {
                var _a, _b;
                const html = (_b = (_a = event.clipboardData) === null || _a === void 0 ? void 0 : _a.getData("text/html")) !== null && _b !== void 0 ? _b : "";
                if (html.includes("<img")) {
                    setAttemptedImgUpload(true);
                    return true;
                }
                setAttemptedImgUpload(false);
                return false;
            },
            transformPasted: (slice) => new Slice(stripNonBreakingSpaces(slice.content), slice.openStart, slice.openEnd),
        },
    });
    useEffect(() => {
        if (!editor || value === undefined) {
            return;
        }
        if (editor.getHTML() !== value) {
            editor.commands.setContent(value);
        }
    }, [value, editor]);
    const handleImageUpload = () => {
        filepicker.pickAndStore(pickerOptions, storeOptions, (blobs) => {
            const blob = blobs[0];
            const storeUrl = filestackEndpoints(imageWidth, fit, blob);
            const sanitizedUrl = sanitizeUrl(blob.key);
            filepicker.storeUrl(storeUrl, { filename: sanitizedUrl, path: FILESTACK_IMAGE_PATH }, (s3Blob) => {
                editor === null || editor === void 0 ? void 0 : editor.chain().focus().setImage({ src: `${S3_URL}${s3Blob.key}` }).run();
            });
        }, () => {
            setUploadError(true);
        });
    };
    const openLinkModal = useCallback(() => {
        var _a;
        const existingUrl = (editor === null || editor === void 0 ? void 0 : editor.isActive("link")) ? ((_a = editor.getAttributes("link").href) !== null && _a !== void 0 ? _a : "") : "";
        setModal({ type: "link", initialUrl: existingUrl });
    }, [editor]);
    const confirmLink = (url) => {
        setModal(null);
        if (url) {
            editor === null || editor === void 0 ? void 0 : editor.chain().focus().unsetColor().setLink({ href: url }).run();
        }
        else {
            editor === null || editor === void 0 ? void 0 : editor.chain().focus().unsetLink().run();
        }
    };
    const confirmMedia = (url) => {
        const embedUrl = toEmbedUrl(url.trim());
        if (!embedUrl) {
            setModal({ type: "media", error: "Only YouTube and Vimeo URLs are supported." });
            return;
        }
        setModal(null);
        editor === null || editor === void 0 ? void 0 : editor.chain().focus().insertContent({
            type: "iframe",
            attrs: { src: embedUrl, width: "100%", height: "315" },
        }).run();
    };
    const handleToggleSource = () => {
        var _a;
        if (!showSource) {
            setSourceHtml(beautifyHtml((_a = editor === null || editor === void 0 ? void 0 : editor.getHTML()) !== null && _a !== void 0 ? _a : "", { indent_size: 2, wrap_line_length: 0 }));
        }
        else {
            editor === null || editor === void 0 ? void 0 : editor.commands.setContent(DOMPurify.sanitize(sourceHtml, {
                ADD_TAGS: ["iframe"],
                ADD_ATTR: ["target", "rel", "allowfullscreen", "frameborder", "src"],
            }));
        }
        setShowSource((s) => !s);
    };
    const handleBlockFormat = (e) => {
        const val = e.target.value;
        setActiveBlock(val);
        if (val === "h1") {
            editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleHeading({ level: 1 }).run();
        }
        else if (val === "h2") {
            editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleHeading({ level: 2 }).run();
        }
        else if (val === "h3") {
            editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleHeading({ level: 3 }).run();
        }
        else if (val === "h4") {
            editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleHeading({ level: 4 }).run();
        }
        else if (val === "h5") {
            editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleHeading({ level: 5 }).run();
        }
        else if (val === "h6") {
            editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleHeading({ level: 6 }).run();
        }
        else if (val === "pre") {
            editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleCodeBlock().run();
        }
        else {
            editor === null || editor === void 0 ? void 0 : editor.chain().focus().setParagraph().run();
        }
    };
    const cmd = (fn) => (e) => {
        e.preventDefault();
        fn();
    };
    return (_jsxs("div", { className: "text-editor", children: [(modal === null || modal === void 0 ? void 0 : modal.type) === "link" && (_jsx(ToolbarModal, { title: "Insert link", label: "URL", placeholder: "https://example.com", initialValue: modal.initialUrl, onConfirm: confirmLink, onCancel: () => setModal(null) })), (modal === null || modal === void 0 ? void 0 : modal.type) === "media" && (_jsx(ToolbarModal, { title: "Insert video", label: "YouTube or Vimeo URL", placeholder: "https://youtube.com/watch?v=...", error: modal.error, onConfirm: confirmMedia, onCancel: () => setModal(null) })), attemptedImgUpload && (_jsx("p", { className: "text-editor__error", children: "Please upload image(s) using image uploader." })), uploadError && _jsx("p", { className: "text-editor__error", children: "Image upload failed. Please try again." }), _jsxs("div", { className: "text-editor__toolbar", children: [has("blocks") && (_jsxs("select", { className: "text-editor__block-select", value: activeBlock, onChange: handleBlockFormat, children: [_jsx("option", { value: "p", children: "Paragraph" }), _jsx("option", { value: "h1", children: "Heading 1" }), _jsx("option", { value: "h2", children: "Heading 2" }), _jsx("option", { value: "h3", children: "Heading 3" }), _jsx("option", { value: "h4", children: "Heading 4" }), _jsx("option", { value: "h5", children: "Heading 5" }), _jsx("option", { value: "h6", children: "Heading 6" }), _jsx("option", { value: "pre", children: "Preformatted" })] })), has("blocks") && hasAny("bold", "italic") && _jsx(Tsep, {}), has("bold") && (_jsx(Tbtn, { active: editor === null || editor === void 0 ? void 0 : editor.isActive("bold"), title: "Bold", onMouseDown: cmd(() => editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleBold().run()), children: _jsx("strong", { children: "B" }) })), has("italic") && (_jsx(Tbtn, { active: editor === null || editor === void 0 ? void 0 : editor.isActive("italic"), title: "Italic", onMouseDown: cmd(() => editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleItalic().run()), children: _jsx("em", { children: "I" }) })), hasAny("bold", "italic") && has("lists") && _jsx(Tsep, {}), has("lists") && (_jsxs(_Fragment, { children: [_jsx(Tbtn, { active: editor === null || editor === void 0 ? void 0 : editor.isActive("bulletList"), title: "Bullet list", onMouseDown: cmd(() => editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleBulletList().run()), children: _jsx(BulletListIcon, {}) }), _jsx(Tbtn, { active: editor === null || editor === void 0 ? void 0 : editor.isActive("orderedList"), title: "Ordered list", onMouseDown: cmd(() => editor === null || editor === void 0 ? void 0 : editor.chain().focus().toggleOrderedList().run()), children: _jsx(OrderedListIcon, {}) })] })), has("lists") && has("color") && _jsx(Tsep, {}), has("color") && (_jsxs("div", { className: "text-editor__color-wrap", children: [_jsx(Tbtn, { active: showColorPicker, title: "Text colour", onMouseDown: (e) => {
                                    e.preventDefault();
                                    setShowColorPicker((s) => {
                                        if (!s && editor) {
                                            const { from, to } = editor.state.selection;
                                            savedSelectionRef.current = { from, to };
                                        }
                                        else {
                                            setShowSketchPicker(false);
                                        }
                                        return !s;
                                    });
                                }, children: _jsx("span", { className: "text-editor__color-icon", children: "A" }) }), showColorPicker && (_jsxs("div", { className: "text-editor__color-palette", children: [documentColors.map((color) => (_jsx("button", { type: "button", title: color, "aria-label": color, style: { background: color }, onMouseDown: (e) => {
                                            e.preventDefault();
                                            editor === null || editor === void 0 ? void 0 : editor.chain().focus().setColor(color).run();
                                            setShowColorPicker(false);
                                        } }, color))), COLORS.map((color) => (_jsx("button", { type: "button", title: color, "aria-label": color, style: { background: color }, onMouseDown: (e) => {
                                            e.preventDefault();
                                            editor === null || editor === void 0 ? void 0 : editor.chain().focus().setColor(color).run();
                                            setShowColorPicker(false);
                                        } }, color))), _jsx("button", { type: "button", className: "text-editor__color-custom", title: "Custom colour", "aria-label": "Custom colour", onMouseDown: (e) => {
                                            e.preventDefault();
                                            setShowSketchPicker((s) => !s);
                                        }, children: "+" })] })), showSketchPicker && (_jsxs("div", { className: "text-editor__color-sketch-wrap", children: [_jsx(SketchPicker, { color: sketchColor, onChange: (c) => setSketchColor(c.hex.toLowerCase()), disableAlpha: true }), _jsx("button", { type: "button", className: "text-editor__color-sketch-apply", onMouseDown: (e) => {
                                            e.preventDefault();
                                            const sel = savedSelectionRef.current;
                                            const chain = editor === null || editor === void 0 ? void 0 : editor.chain().focus();
                                            if (sel) {
                                                chain === null || chain === void 0 ? void 0 : chain.setTextSelection(sel);
                                            }
                                            chain === null || chain === void 0 ? void 0 : chain.setColor(sketchColor).run();
                                            setDocumentColors((prev) => {
                                                const isNew = !COLORS.includes(sketchColor) && !prev.includes(sketchColor);
                                                return isNew ? [...prev, sketchColor] : prev;
                                            });
                                            setShowSketchPicker(false);
                                            setShowColorPicker(false);
                                        }, children: "APPLY" })] }))] })), has("color") && hasAny("image", "media", "link") && _jsx(Tsep, {}), !has("color") && has("lists") && hasAny("image", "media", "link") && _jsx(Tsep, {}), has("image") && (_jsx(Tbtn, { title: "Insert image", onMouseDown: cmd(handleImageUpload), children: _jsx(ImageIcon, {}) })), has("media") && (_jsx(Tbtn, { title: "Insert video (YouTube / Vimeo)", onMouseDown: cmd(() => setModal({ type: "media" })), children: _jsx(MediaIcon, {}) })), has("link") && (_jsx(Tbtn, { active: editor === null || editor === void 0 ? void 0 : editor.isActive("link"), title: "Insert link", onMouseDown: cmd(openLinkModal), children: _jsx(LinkIcon, {}) })), hasAny("image", "media", "link") && has("align") && _jsx(Tsep, {}), !hasAny("image", "media", "link") && has("lists") && has("align") && _jsx(Tsep, {}), has("align") && (_jsxs(_Fragment, { children: [_jsx(Tbtn, { active: editor === null || editor === void 0 ? void 0 : editor.isActive({ textAlign: "left" }), title: "Align left", onMouseDown: cmd(() => editor === null || editor === void 0 ? void 0 : editor.chain().focus().setTextAlign("left").run()), children: _jsx(AlignLeftIcon, {}) }), _jsx(Tbtn, { active: editor === null || editor === void 0 ? void 0 : editor.isActive({ textAlign: "center" }), title: "Align centre", onMouseDown: cmd(() => editor === null || editor === void 0 ? void 0 : editor.chain().focus().setTextAlign("center").run()), children: _jsx(AlignCenterIcon, {}) }), _jsx(Tbtn, { active: editor === null || editor === void 0 ? void 0 : editor.isActive({ textAlign: "right" }), title: "Align right", onMouseDown: cmd(() => editor === null || editor === void 0 ? void 0 : editor.chain().focus().setTextAlign("right").run()), children: _jsx(AlignRightIcon, {}) })] })), has("align") && has("hr") && _jsx(Tsep, {}), !has("align") && hasAny("image", "media", "link") && has("hr") && _jsx(Tsep, {}), has("hr") && (_jsx(Tbtn, { title: "Horizontal rule", onMouseDown: cmd(() => editor === null || editor === void 0 ? void 0 : editor.chain().focus().setHorizontalRule().run()), children: _jsx(HrIcon, {}) })), has("hr") && has("emoji") && _jsx(Tsep, {}), has("emoji") && (_jsxs("div", { className: "text-editor__emoji-wrap", children: [_jsx(Tbtn, { active: showEmojis, title: "Insert emoji", onMouseDown: (e) => {
                                    e.preventDefault();
                                    setShowEmojis((s) => !s);
                                }, children: _jsx(EmojiIcon, {}) }), showEmojis && (_jsx("div", { className: "text-editor__emoji-picker", children: EMOJIS.map((emoji) => (_jsx("button", { type: "button", onMouseDown: (e) => {
                                        e.preventDefault();
                                        editor === null || editor === void 0 ? void 0 : editor.chain().focus().insertContent(emoji).run();
                                        setShowEmojis(false);
                                    }, children: emoji }, emoji))) }))] })), has("emoji") && has("code") && _jsx(Tsep, {}), !has("emoji") && has("hr") && has("code") && _jsx(Tsep, {}), has("code") && (_jsx(Tbtn, { active: showSource, title: "Source code", onMouseDown: cmd(handleToggleSource), children: _jsx(CodeIcon, {}) })), has("history") && (_jsxs("div", { className: "text-editor__history", children: [_jsx(Tbtn, { title: "Undo", onMouseDown: cmd(() => editor === null || editor === void 0 ? void 0 : editor.chain().focus().undo().run()), children: _jsx(UndoIcon, {}) }), _jsx(Tbtn, { title: "Redo", onMouseDown: cmd(() => editor === null || editor === void 0 ? void 0 : editor.chain().focus().redo().run()), children: _jsx(RedoIcon, {}) })] }))] }), _jsx("div", { className: "text-editor__content", style: {
                    "--editor-min-height": `${minHeight}px`,
                    "--editor-max-height": `${maxHeight}px`,
                }, children: showSource ? (_jsx("textarea", { className: "text-editor__source", value: sourceHtml, onChange: (e) => setSourceHtml(e.target.value), style: { minHeight } })) : (_jsx(EditorContent, { editor: editor })) })] }));
};
export default WysiwygEditor;
